/**
 * Type definitions for the API client
 */

/**
 * Connection status change event type
 */
export type ConnectionStatusChangeEvent = "connected" | "disconnected"

/**
 * Email verification type
 */
export enum VerificationType {
  SIGNUP = "signup",
  RESET = "reset",
}

/**
 * Auth API interface
 */
export interface AuthApi {
  /**
   * Request email verification
   */
  requestVerification: (
    email: string,
    type: VerificationType,
  ) => Promise<{ success: boolean; token: string }>

  /**
   * Check if email is already registered
   */
  checkEmail: (email: string) => Promise<{ exists: boolean }>

  /**
   * Verify email with code
   */
  verifyEmail: (
    email: string,
    token: string,
    code: string,
    type: VerificationType,
  ) => Promise<{ success: boolean }>

  /**
   * Sign up with verified email
   */
  signup: (
    email: string,
    verificationToken: string,
    code: string,
    authPublicKey: string,
  ) => Promise<{ success: boolean; token: string }>

  /**
   * Reset account
   */
  resetAccount: (
    email: string,
    verificationToken: string,
    code: string,
    authPublicKey: string,
  ) => Promise<{ success: boolean; token: string }>

  /**
   * Request a challenge for login
   */
  challenge: (email: string) => Promise<{ success: boolean; challenge: string }>

  /**
   * Login with email and signature
   */
  login: (
    email: string,
    signature: string,
  ) => Promise<{ success: boolean; token: string }>
}

/**
 * API client interface
 */
export interface IApiClient {
  auth: AuthApi
  setToken(token: string | null): void
  queryClient: any

  /**
   * Check if client is connected to the network
   */
  readonly isConnected: boolean

  /**
   * Check server connection
   */
  checkConnection(): Promise<boolean>

  /**
   * Add event listener
   */
  on(event: string, listener: (...args: any[]) => void): this

  /**
   * Remove event listener
   */
  off(event: string, listener: (...args: any[]) => void): this

  /**
   * Clean up resources
   */
  dispose(): void
}
